import * as React from 'react';

import {RouteComponentProps, withRouter} from 'react-router';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import {ErrorAlert} from '../common/ErrorAlert';
import {ErrorResponse, SaveWarehouseRequest, Warehouse} from '../api/dtos/dtos';
import {Button, Modal, Table, Tag} from 'antd';
import locales from '../locales';
import {ColumnProps} from 'antd/lib/table';
import {indexRowKeyFn, simpleColumn} from '../utils/table-utils';
import furoApi from '../api/FuroApi';
import {formatDateTimeOrNull} from '../utils/date-utils';
import ButtonBar from '../common/ButtonBar';
import accountProvider from '../account/AccountProvider';
import WarehouseForm from './WarehouseForm';
import {showSaveSuccessNotification} from '../utils/dialogs';
import ButtonGroup from "antd/lib/button/button-group";

interface State {
  error?: ErrorResponse;
  saveError?: ErrorResponse;
  warehouses: Warehouse[];
  editedWarehouse?: Warehouse;
  modalTitle?: string;
  onModalSave?: (request: SaveWarehouseRequest) => void;
}

class WarehousesPage extends React.Component<RouteComponentProps<{}, {}, {}>, State> {

  public readonly loadingHandler?: LoadingOverlayHandler;

  constructor(props: RouteComponentProps<{}, {}, {}>) {
    super(props);
    this.state = {
      warehouses: [],
    };
  }

  public componentDidMount() {
    this.loadWarehouses();
  }

  public render() {
    const isAdmin = accountProvider.isAdmin();
    const columns: Array<ColumnProps<Warehouse>> = [
      simpleColumn(locales.name, r => this.renderName(r),),
      simpleColumn(locales.created_at, r => this.renderCreatedAt(r)),
      {key: 'actions', width: 48, render: this.renderActionColumn},
    ];

    const {warehouses, error, saveError, editedWarehouse, onModalSave, modalTitle} = this.state;

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        {isAdmin && (
          <ButtonBar style={{marginBottom: 8}}>
            <Button size="small" type="primary" icon="plus" onClick={this.onNewWarehouse}>
              {locales.add}
            </Button>
          </ButtonBar>
        )}

        <Table
          style={{marginBottom: 24}}
          columns={columns}
          dataSource={warehouses}
          rowKey={indexRowKeyFn}
          bordered={true}
          pagination={false}
          size="small"
          scroll={{x: 442}}
        />

        <Modal
          title={modalTitle}
          visible={editedWarehouse !== undefined}
          footer={null}
          destroyOnClose={true}
          onCancel={this.onModalCancel}
        >
          {editedWarehouse && <WarehouseForm
              warehouse={editedWarehouse.id ? editedWarehouse : undefined}
              forcedDefault={this.shouldForceDefault()}
              onFormSubmit={onModalSave}
              onFormCancel={this.onModalCancel}
          />}
          {saveError && <ErrorAlert style={{marginTop: '16px'}} error={saveError}/>}
        </Modal>

        {error && <ErrorAlert style={{marginTop: '16px'}} error={error}/>}
      </>
    );
  }

  private onNewWarehouse = () => {
    this.setState({
      editedWarehouse: {
        name: '',
        isDefault: false,
      },
      onModalSave: this.onWarehouseSave,
      modalTitle: locales.warehouse_create,
      saveError: undefined,
    });
  };

  private onEditWarehouseClick = (warehouse: Warehouse) => {
    if (!accountProvider.isAdmin()) {
      return;
    }
    this.setState({
      editedWarehouse: warehouse,
      onModalSave: this.onWarehouseSave,
      modalTitle: locales.warehouse_edit,
      saveError: undefined,
    });
  };

  private shouldForceDefault = (): boolean => {
    const {warehouses, editedWarehouse} = this.state;
    if (!editedWarehouse) {
      return false;
    }

    const isCreateMode = editedWarehouse.id === undefined;
    if (isCreateMode) {
      // Force default only when creating the very first warehouse.
      return warehouses.length === 0;
    }

    // In edit mode, force default only if this is the only warehouse.
    return warehouses.length === 1;
  };

  private renderName = (warehouse: Warehouse): React.ReactNode => {
    return (<>
        {warehouse.name}
        {warehouse.isDefault && <Tag style={{marginLeft: 8}} color="blue">{locales.default_tag}</Tag>}
      </>
    );
  };

  private renderCreatedAt = (warehouse: Warehouse): string => {
    const createdAt = warehouse.createdAt;
    return formatDateTimeOrNull(createdAt) || '-';
  };

  private renderActionColumn = (_: any, record: Warehouse, __: number) => {
    const onEditWarehouseClick = () => this.onEditWarehouseClick(record);
    return <div className="action-column" style={{textAlign: 'right'}}>
      <ButtonGroup>
        <Button icon="edit"
                onClick={onEditWarehouseClick}
                title={locales.edit}/>
      </ButtonGroup>
    </div>;
  };


  private loadWarehouses = () => {
    const promise = furoApi.warehouses.getWarehouses()
      .then(this.onLoadWarehousesSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onWarehouseSave = (request: SaveWarehouseRequest) => {
    const promise = furoApi.warehouses.saveWarehouse(request)
      .then(this.onWarehouseSaved)
      .catch(this.onSaveApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onWarehouseSaved = () => {
    showSaveSuccessNotification();
    this.loadWarehouses();
  };

  private onLoadWarehousesSuccess = (response: Warehouse[]) => {
    this.setState({
      warehouses: response,
      editedWarehouse: undefined,
      error: undefined,
      saveError: undefined,
    });
  };

  private onModalCancel = () => {
    this.setState({editedWarehouse: undefined, saveError: undefined});
  };

  private onApiError = (error: ErrorResponse) => {
    this.setState({error});
  };

  private onSaveApiError = (error: ErrorResponse) => {
    this.setState({saveError: error});
  };
}

export default withRouter(WarehousesPage);




// WEBPACK FOOTER //
// ./src/warehouse/WarehousesPage.tsx